import { FileUpload, SingleSelect } from "@bonliva-crm/ui";
import locale from "@bonliva-crm/locale";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  makeStyles,
  Tooltip,
  Typography
} from "@material-ui/core";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import React, { useEffect } from "react";
import {
  ConsultantReferenceRelation,
  ConsultantReferenceStatus
} from "@bonliva-crm/shared";
import { DevTool } from "@hookform/devtools";
import { ChangeStatusConsultantReferenceInput, ReferenceItemFragment } from "@graphql";
import moment from "moment";
import { OpenInNew } from "@material-ui/icons";
import cns from "classnames";

type Props = {
  data: ReferenceItemFragment;
  references: ReferenceItemFragment[];
  open: boolean;
  setOpen: (open: boolean) => void;
  onSubmit: (
    refId: string,
    input: ChangeStatusConsultantReferenceInput,
    file?: File
  ) => void;
  loading?: boolean;
};

export const ReferenceStatusModal = ({
  data,
  open,
  setOpen,
  onSubmit,
  loading,
  references
}: Props) => {
  const classes = useStyles();
  const [file, setFile] = React.useState<File | null>(null);
  const form = useForm<any>({
    defaultValues: {
      workRelation: data.workRelation
        ? {
            label: locale.consultantReferenceRelation[data.workRelation],
            value: data.workRelation
          }
        : null
    },
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const handleSubmit = (status: ConsultantReferenceStatus) => {
    const _input = {
      ...form.getValues(),
      workRelation: form.getValues().workRelation?.value,
      status
    };
    onSubmit(data._id, _input, file);
  };

  const requiredRelations = [
    ConsultantReferenceRelation.CurrentBoss,
    ConsultantReferenceRelation.LatestBoss,
    ConsultantReferenceRelation.CurrentColleague
  ];

  const showAdjustWorkRelationHistory = references
    .filter((x) => requiredRelations.includes(x.workRelation))
    .some((x) => x.workRelation === form.getValues().workRelation?.value);
  const workRelation = form.watch("workRelation");

  useEffect(() => {
    if (showAdjustWorkRelationHistory) {
      form.setValue("adjustWorkRelationHistory", true);
    } else {
      form.setValue("adjustWorkRelationHistory", false);
    }
  }, [showAdjustWorkRelationHistory, workRelation]);

  useEffect(() => {
    if (open) {
      form.trigger();
    } else {
      form.reset();
    }
  }, [open]);

  const canSubmit = form.formState.isValid && !loading;
  const hasSubmittedForm = !!data.form?.submittedAt;

  return (
    <FormProvider {...form}>
      <Dialog open={open} maxWidth="md" fullWidth onClose={() => setOpen(false)}>
        <DevTool control={form.control} placement={"top-right"} />
        <DialogTitle>
          <Typography variant={"h3"}>
            {data.name}{" "}
            {data.formType ? locale.consultantReferenceType[data.formType] : ""}
          </Typography>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant={"body1"}>{data.position}</Typography>
              <Typography variant={"body1"}>{data.workPlace}</Typography>
              <Typography variant={"body1"}>
                Senast redigerad: {moment(data.updatedOn).format("L")}
              </Typography>
              <Typography variant={"body1"}>
                {locale.consultantReferenceStatus[data.status]}
              </Typography>
              {data.document && (
                <Typography
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 5
                  }}
                  variant={"body1"}
                  onClick={() => window.open(data.document.PreviewURL)}
                >
                  {locale.File}: <strong>{data.document?.FileRef.Name} </strong>
                  <OpenInNew fontSize="small" color={"action"} />
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="workRelation"
                control={form.control}
                render={({ field, fieldState }) => (
                  <SingleSelect
                    {...field}
                    error={!!fieldState.error}
                    inputLabel={locale.WorkRelationship}
                    menuPortalTarget={document.body}
                    styleProps={{
                      control: { showBorder: true },
                      menu: { showBorder: true },
                      menuPortal: { zIndex: 9999 }
                    }}
                    placeholder=""
                    isClearable
                    onClear={() => field.onChange(null)}
                    options={Object.values(ConsultantReferenceRelation).toLabelValue(
                      (x) => locale.consultantReferenceRelation[x]
                    )}
                  />
                )}
              />
            </Grid>
            {showAdjustWorkRelationHistory && (
              <Grid item xs={12}>
                <Controller
                  control={form.control}
                  name="adjustWorkRelationHistory"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          color="primary"
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label={locale.AdjustWorkRelationHistoryAutomatically}
                    />
                  )}
                />
              </Grid>
            )}
            <Tooltip
              title={
                hasSubmittedForm
                  ? "Du kan inte ladda upp en extern fil då det redan finns en formulär inskickat från referenten."
                  : ""
              }
            >
              <Grid item xs={12}>
                <div
                  className={cns({
                    [classes.disabled]: hasSubmittedForm
                  })}
                >
                  <FileUpload
                    onFileRemove={() => {
                      setFile(null);
                    }}
                    onFileUpload={(file) => {
                      setFile(file);
                    }}
                  />
                </div>
              </Grid>
            </Tooltip>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Box className={classes.btnWrap}>
            <Button
              color={"primary"}
              className={classes.cancelBtn}
              onClick={() => setOpen(false)}
            >
              {locale.Cancel}
            </Button>
            <Button
              color="primary"
              variant={"outlined"}
              disabled={!canSubmit || !data.form}
              onClick={() => handleSubmit(ConsultantReferenceStatus.NotApproved)}
            >
              {locale.Rejected}
            </Button>
            <Button
              color="primary"
              variant={"outlined"}
              // disabled={!canSubmit}
              onClick={() => handleSubmit(ConsultantReferenceStatus.RequestNotAnswered)}
            >
              {"Ej svar - beställ på nytt om aktuellt"}
            </Button>
            <Button
              color="primary"
              variant={"outlined"}
              disabled={!canSubmit}
              onClick={() =>
                handleSubmit(ConsultantReferenceStatus.ApprovedWithException)
              }
            >
              {"Godkänd undantag"}
            </Button>
            <Button
              color="primary"
              variant={"contained"}
              disabled={!canSubmit || !data.form || (!data.document && !file)}
              onClick={() => {
                handleSubmit(ConsultantReferenceStatus.Approved);
              }}
            >
              {locale.Approve}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

const schema = yup.object().shape({
  workRelation: yup.object().shape({
    value: yup.string().required()
  }),
  adjustWorkRelationHistory: yup.boolean().nullable(true)
});

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    overflow: "hidden"
  },
  btnWrap: {
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
    padding: theme.spacing(2),
    gap: theme.spacing(2)
  },
  cancelBtn: {
    marginRight: "auto"
  },

  disabled: {
    pointerEvents: "none",
    opacity: 0.5
  }
}));
